import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { cfsTabs } from '@/constants';
import { useAgencyStore, useCFSNavigationStore } from '@/store';
import { isOpenedInNewTab } from '@/utils';
import { refreshCFSNavigation } from '@/utils/cfsNavigation';

export const useCFSNavigation = (cfsId: string) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { agencies } = useAgencyStore();

  // Use selectors to only re-render when derived values change (not on every list update)
  const nextCfsId = useCFSNavigationStore(
    useCallback((state) => state.getNextCFSId(cfsId), [cfsId]),
  );
  const prevCfsId = useCFSNavigationStore(
    useCallback((state) => state.getPreviousCFSId(cfsId), [cfsId]),
  );
  const currentIndex = useCFSNavigationStore(
    useCallback((state) => state.getCurrentIndex(cfsId), [cfsId]),
  );
  const totalCount = useCFSNavigationStore((state) => state.getTotalCount());
  const navigationContext = useCFSNavigationStore(
    (state) => state.navigationContext,
  );

  // Get current tab from URL path
  const getCurrentTabPath = useCallback(() => {
    const pathParts = pathname.split('/').filter(Boolean);
    const lastPart = pathParts[pathParts.length - 1];
    return cfsTabs.some((tab) => tab.path === lastPart) ? lastPart : null;
  }, [pathname]);

  // Navigate to a CFS while preserving current tab and new-tab mode param
  // Arrows preserve the current URL pattern, only changing the ID
  const navigateToCFS = useCallback(
    (targetCfsId: string) => {
      const tabPath = getCurrentTabPath();
      const currentPath = window.location.pathname;
      const searchParams = new URLSearchParams(window.location.search);
      const isNewTab = isOpenedInNewTab(searchParams);

      // Supported URL patterns (order matters - longer patterns first)
      const patterns = [
        '/cfs-active/slide',
        '/cfs-active',
        '/cfs/slide',
        '/cfs',
      ];
      const basePath =
        patterns.find((p) => currentPath.startsWith(`${p}/`)) ?? '/cfs';
      let newPath = `${basePath}/${targetCfsId}`;

      // Append tab path if present
      if (tabPath) {
        newPath = `${newPath}/${tabPath}`;
      }

      // Preserve new-tab mode and search params
      const params = new URLSearchParams();
      if (isNewTab) {
        params.set('newTab', 'true');
      }
      const searchValue = searchParams.get('search');
      if (searchValue) {
        params.set('search', searchValue);
      }
      const queryString = params.toString();
      if (queryString) {
        newPath = `${newPath}?${queryString}`;
      }

      navigate(newPath);
    },
    [navigate, getCurrentTabPath],
  );

  const goToNext = useCallback(() => {
    const navigateToResolvedCfs = async () => {
      let targetCfsId = useCFSNavigationStore.getState().getNextCFSId(cfsId);

      if (!targetCfsId && navigationContext?.sourceRoute) {
        await refreshCFSNavigation({
          currentCfsId: cfsId,
          agencies,
          navigationContext,
        });
        targetCfsId = useCFSNavigationStore.getState().getNextCFSId(cfsId);
      }

      if (targetCfsId) {
        navigateToCFS(targetCfsId);
      }
    };

    void navigateToResolvedCfs();
  }, [agencies, cfsId, navigateToCFS, navigationContext]);

  const goToPrevious = useCallback(() => {
    const navigateToResolvedCfs = async () => {
      let targetCfsId = useCFSNavigationStore
        .getState()
        .getPreviousCFSId(cfsId);

      if (!targetCfsId && navigationContext?.sourceRoute) {
        await refreshCFSNavigation({
          currentCfsId: cfsId,
          agencies,
          navigationContext,
        });
        targetCfsId = useCFSNavigationStore.getState().getPreviousCFSId(cfsId);
      }

      if (targetCfsId) {
        navigateToCFS(targetCfsId);
      }
    };

    void navigateToResolvedCfs();
  }, [agencies, cfsId, navigateToCFS, navigationContext]);

  return {
    canGoNext: !!nextCfsId,
    canGoPrevious: !!prevCfsId,
    goToNext,
    goToPrevious,
    currentIndex,
    totalCount,
    hasNavigationContext: currentIndex !== -1 && totalCount > 0,
  };
};
